import { Command, Option } from "clipanion";
import { DefaultAzureCredential, ManagedIdentityCredential, type TokenCredential } from "@azure/identity";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { CLI_VERSION } from "../../lib/package-version.js";
import { readStampOutcome, writeStamp } from "../../lib/platform-stamp.js";
import { discoverStampStorage } from "../../lib/platform-storage-discovery.js";
import { buildRunningBuildStamp } from "../../lib/platform-stamp-build.js";
import { renderJson, resolveOutputMode } from "../../lib/output.js";

export class PlatformStampBuildCommand extends M8tCommand {
  static paths = [["platform", "stamp-build"]];
  static usage = Command.Usage({
    description: "Record the currently running build as this install's platform version.",
    details:
      "For installs that are deployed continuously rather than from a published release. Unlike the install-time record, this overwrites on every run — the running build is the truth. Only components this writer deploys are reported; the rest are marked as managed elsewhere.",
  });

  version = Option.String("--version", { description: "The running build's own version, bare X.Y.Z." });
  gatewayTag = Option.String("--gateway-tag", { description: "The tag of the gateway image this build deployed." });
  gatewayDigest = Option.String("--gateway-digest", { description: "The digest of the gateway image this build deployed." });
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  miClientId = Option.String("--mi-client-id", { description: "Authenticate as this user-assigned managed identity instead of the ambient credential." });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim().length === 0) {
        throw new LocalCliError({ code: "PLATFORM_STAMP_BUILD_ARG_MISSING", message: `${flag} is required.` });
      }
      return v.trim();
    };
    // Progress goes to STDERR so `--output json` keeps a clean, parseable stdout.
    const onProgress = (m: string) => { this.context.stderr.write(`${m}\n`); };

    const credential: TokenCredential =
      typeof this.miClientId === "string" && this.miClientId.trim().length > 0
        ? new ManagedIdentityCredential({ clientId: this.miClientId.trim() })
        : new DefaultAzureCredential();

    const ctx = {
      credential,
      subscriptionId: need(this.subscription, "--subscription"),
      resourceGroup: need(this.resourceGroup, "--resource-group"),
    };

    const { accountName } = await discoverStampStorage(ctx);

    // A three-way read, not a best-effort one: the previous-version field
    // below becomes another part of the system's rollback target, and the
    // persona/infra/seed fields are carried forward untouched rather than
    // defaulted. Both are wrong if "unreadable" collapses into "absent" — an
    // unreadable read must refuse the write rather than publish "this
    // install has never had a prior version" (and blank three fields it does
    // not own) as fact.
    const existing = await readStampOutcome(ctx);
    if (existing.source === "unreadable") {
      throw new LocalCliError({
        code: "PLATFORM_STAMP_BUILD_PRIOR_UNREADABLE",
        message: "Could not read the existing platform stamp; refusing to overwrite it blind.",
        hint: "The running build is the truth about the CURRENT version, not about whether a previous one existed — retry once the prior record can be read.",
      });
    }
    const previousPlatformVersion = existing.source === "explicit" ? existing.stamp.platformVersion : null;
    const priorComponents = existing.source === "explicit" ? existing.stamp.components : undefined;

    const stamp = buildRunningBuildStamp({
      version: need(this.version, "--version"),
      gatewayTag: need(this.gatewayTag, "--gateway-tag"),
      gatewayDigest: this.gatewayDigest ?? "",
      previousPlatformVersion,
      cliVersion: CLI_VERSION,
      nowIso: new Date().toISOString(),
      personas: priorComponents?.personas ?? {},
      infraTreeSha: priorComponents?.infra.treeSha ?? "",
      ...(priorComponents?.brainSeeds ? { brainSeeds: priorComponents.brainSeeds } : {}),
    });

    // Unconditional, unlike the install-time record: the running build is the truth on
    // every roll, so this always writes — there is no only-if-absent guard here.
    await writeStamp({ ...ctx, stamp, onProgress });

    const result = { platformVersion: stamp.platformVersion, previous: previousPlatformVersion, storageAccount: accountName };
    this.context.stdout.write(
      mode === "json"
        ? renderJson(result) + "\n"
        : `platform stamp: ${result.platformVersion} (previous ${result.previous ?? "none"}, storage ${result.storageAccount})\n`,
    );
    return 0;
  }
}
